import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import { DEFAULT_THEME } from "../constants/theme";

export default function CustomDatePicker({ value, onChange, className, customTrigger, type = "date", openDirection = "down", theme = DEFAULT_THEME }) {
    // type: "date" (default, YYYY-MM-DD), "month" (YYYY-MM), "year" (YYYY)
    const [isOpen, setIsOpen] = useState(false);
    const [viewMode, setViewMode] = useState("days"); // days, months, years
    const [currentDate, setCurrentDate] = useState(new Date());
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);

    // 초기화: type에 따른 기본 viewMode 설정
    useEffect(() => {
        if (!isOpen) {
            // 닫힐 때 혹은 초기화 될 때 뷰 모드 리셋
            if (type === "year") setViewMode("years");
            else if (type === "month") setViewMode("months");
            else setViewMode("days");
        }
    }, [isOpen, type]);

    // 외부 클릭 감지 및 ESC 키 감지
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        const handleEscKey = (event) => {
            if (event.key === "Escape" && isOpen) {
                setIsOpen(false);
                event.stopPropagation();
            }
        };

        const handleScroll = () => {
            if (isOpen) setIsOpen(false); // 스크롤 시 닫기 (위치 틀어짐 방지)
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscKey, true);
        window.addEventListener("scroll", handleScroll, true);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscKey, true);
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, [isOpen]);

    // value 변경 시 내부 달력 날짜도 동기화
    useEffect(() => {
        if (value) {
            // value가 "2024" (년도만) 혹은 "2024-05" (월까지만) 일 수 있음
            // Date 생성자가 이를 적절히 파싱하도록 처리
            let dateStr = value;
            if (type === 'month' && value.length === 7) dateStr = `${value}-01`;
            if (type === 'year' && value.length === 4) dateStr = `${value}-01-01`;

            const parsedDate = new Date(dateStr);
            if (!isNaN(parsedDate)) {
                setCurrentDate(parsedDate);
            }
        }
    }, [value, type]);

    const formatDate = (date, formatType = type) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');

        if (formatType === 'year') return `${y}`;
        if (formatType === 'month') return `${y}-${m}`;
        return `${y}-${m}-${d}`;
    };

    const handlePrev = () => {
        const newDate = new Date(currentDate);
        if (viewMode === "days") {
            newDate.setMonth(newDate.getMonth() - 1);
        } else if (viewMode === "months") {
            newDate.setFullYear(newDate.getFullYear() - 1);
        } else {
            newDate.setFullYear(newDate.getFullYear() - 10);
        }
        setCurrentDate(newDate);
    };

    const handleNext = () => {
        const newDate = new Date(currentDate);
        if (viewMode === "days") {
            newDate.setMonth(newDate.getMonth() + 1);
        } else if (viewMode === "months") {
            newDate.setFullYear(newDate.getFullYear() + 1);
        } else {
            newDate.setFullYear(newDate.getFullYear() + 10);
        }
        setCurrentDate(newDate);
    };

    const handleDayClick = (day) => {
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        onChange(formatDate(newDate));
        setIsOpen(false);
    };

    const handleMonthClick = (monthIndex) => {
        const newDate = new Date(currentDate.getFullYear(), monthIndex, 1);
        if (type === 'month') {
            onChange(formatDate(newDate));
            setIsOpen(false);
        } else {
            setCurrentDate(newDate);
            setViewMode("days");
        }
    };

    const handleYearClick = (year) => {
        const newDate = new Date(year, currentDate.getMonth(), 1);
        if (type === 'year') {
            onChange(formatDate(newDate));
            setIsOpen(false);
        } else {
            setCurrentDate(newDate);
            setViewMode("months");
        }
    };

    const handleTodayClick = () => {
        const today = new Date();
        onChange(formatDate(today, 'date'));
        setIsOpen(false);
        setCurrentDate(today);
        if (type === 'date') setViewMode("days");
    };

    const renderHeader = () => (
        <div className={`flex justify-between items-center ${isMobile ? 'mb-2 px-0.5' : 'mb-4 px-1'}`}>
            <button type="button" onClick={handlePrev} className={`p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:${theme.text} transition`}><ChevronLeft size={isMobile ? 18 : 20} /></button>
            <div className="flex items-center gap-1">
                {(viewMode === "days" || viewMode === "months") && (
                    <button type="button" onClick={() => setViewMode("years")} className={`font-black ${theme.text} hover:bg-gray-50 px-2 py-1 rounded-lg transition ${isMobile ? 'text-base' : 'text-lg'}`}>
                        {currentDate.getFullYear()}년
                    </button>
                )}
                {viewMode === "years" && (
                    <span className={`font-black ${theme.text} px-2 py-1 ${isMobile ? 'text-base' : 'text-lg'}`}>
                        {Math.floor(currentDate.getFullYear() / 10) * 10} - {Math.floor(currentDate.getFullYear() / 10) * 10 + 9}
                    </span>
                )}

                {viewMode === "days" && (
                    <button type="button" onClick={() => setViewMode("months")} className={`font-black ${theme.text} hover:bg-gray-50 px-2 py-1 rounded-lg transition ${isMobile ? 'text-base' : 'text-lg'}`}>
                        {currentDate.getMonth() + 1}월
                    </button>
                )}
            </div>
            <button type="button" onClick={handleNext} className={`p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:${theme.text} transition`}><ChevronRight size={isMobile ? 18 : 20} /></button>
        </div>
    );

    const renderDays = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const lastDate = new Date(year, month + 1, 0).getDate();

        const days = [];
        for (let i = 0; i < firstDay; i++) days.push(<div key={`empty-${i}`} />);
        for (let i = 1; i <= lastDate; i++) {
            const dateStr = formatDate(new Date(year, month, i), "date"); // Days 비교는 항상 full date
            const isSelected = value === dateStr; // Simple string compare works if value is proper
            const isToday = dateStr === formatDate(new Date(), "date");

            days.push(
                <button
                    key={i}
                    type="button"
                    onClick={() => handleDayClick(i)}
                    className={`
                        ${isMobile ? 'w-6 h-6 text-xs' : 'w-8 h-8 text-sm'} rounded-full flex items-center justify-center font-bold transition mx-auto
                        ${isSelected ? `${theme.bg} text-white shadow-md scale-110` : "text-gray-600 hover:bg-gray-100"}
                        ${isToday && !isSelected ? `${theme.text} ring-1 ${theme.border.replace('border-', 'ring-')}` : ""}
                    `}
                >
                    {i}
                </button>
            );
        }
        return <div className={`grid grid-cols-7 ${isMobile ? 'gap-y-1' : 'gap-y-2'}`}>{days}</div>;
    };

    const renderMonths = () => (
        <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: 12 }, (_, i) => {
                const isSelected = type === 'month' && (currentDate.getFullYear() === new Date(value || new Date()).getFullYear() && i === new Date(value || new Date()).getMonth());
                return (
                    <button key={i} type="button" onClick={() => handleMonthClick(i)} className={`p-3 rounded-xl font-bold text-sm transition ${currentDate.getMonth() === i ? `border-2 ${theme.border} ${theme.text}` : "hover:bg-gray-50 text-gray-600"} ${isSelected ? `${theme.bg} !text-white shadow-md` : ""}`}>
                        {i + 1}월
                    </button>
                )
            })}
        </div>
    );

    const renderYears = () => {
        const startYear = Math.floor(currentDate.getFullYear() / 10) * 10;
        return (
            <div className="grid grid-cols-3 gap-2">
                {Array.from({ length: 12 }, (_, i) => {
                    const year = startYear - 1 + i;
                    const isSelected = type === 'year' && String(year) === String(value);
                    return (
                        <button key={year} type="button" onClick={() => handleYearClick(year)} className={`p-3 rounded-xl font-bold text-sm transition ${currentDate.getFullYear() === year ? `border-2 ${theme.border} ${theme.text}` : "hover:bg-gray-50 text-gray-600"} ${isSelected ? `${theme.bg} !text-white shadow-md` : ""} ${i === 0 || i === 11 ? "opacity-50" : ""}`}>
                            {year}
                        </button>
                    );
                })}
            </div>
        );
    };

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleToggle = (e) => {
        if (!isOpen) {
            // 열기 전에 위치 계산 (동기적 처리)
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                const dropdownWidth = isMobile ? 320 : 260; // 모바일에서는 320px로 확대
                const dropdownHeight = isMobile ? 380 : 320; // 드롭다운 높이 추정
                const screenWidth = window.innerWidth;

                let left = rect.left;
                // 오른쪽 화면 밖으로 나가는 경우 조정 (여백 10px 유지)
                if (left + dropdownWidth > screenWidth - 10) {
                    left = screenWidth - dropdownWidth - 10;
                }
                // 왼쪽 화면 밖으로 나가는 경우 조정
                if (left < 10) left = 10;

                // openDirection에 따라 위/아래로 열기
                let top;
                if (openDirection === 'up') {
                    // 위로 열기: 트리거 위쪽에 드롭다운 배치
                    top = rect.top - dropdownHeight - 8;
                    // 화면 위로 벗어나면 아래로 열기
                    if (top < 10) {
                        top = rect.bottom + 8;
                    }
                } else {
                    // 아래로 열기 (default)
                    top = rect.bottom + 8;
                }

                setPosition({
                    top: top,
                    left: left
                });
            }
            setIsOpen(true);
        } else {
            setIsOpen(false);
        }
    };

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            <div onClick={handleToggle} className="cursor-pointer">
                {customTrigger ? customTrigger : (
                    <div className={`flex items-center gap-2 group px-2 py-1 hover:${theme.text} transition w-fit`}>
                        <CalendarIcon size={18} className={`text-gray-400 group-hover:${theme.text} transition-colors`} />
                        <span className="font-bold text-gray-700 text-sm min-w-[80px] text-center">{value}</span>
                    </div>
                )}
            </div>

            {isOpen && createPortal(
                <div
                    ref={dropdownRef}
                    style={{
                        position: 'fixed',
                        top: position.top,
                        left: position.left,
                        zIndex: 99999
                    }}
                    className={`bg-white rounded-[1.2rem] shadow-2xl border border-gray-100 ${isMobile ? 'p-4 w-[320px]' : 'p-4 w-[260px]'}`}
                >
                    {renderHeader()}
                    {viewMode === "days" && renderDays()}
                    {viewMode === "months" && renderMonths()}
                    {viewMode === "years" && renderYears()}

                    {/* Today Button Footer */}
                    <div className={`${isMobile ? 'mt-2 pt-2' : 'mt-3 pt-3'} border-t border-gray-100 flex justify-center`}>
                        <button
                            type="button"
                            onClick={handleTodayClick}
                            className={`text-xs font-bold ${theme.text} ${theme.text.replace('text-', 'bg-')}/5 rounded-xl hover:${theme.text.replace('text-', 'bg-')}/10 transition flex items-center gap-1 ${isMobile ? 'px-3 py-1.5' : 'px-4 py-2'}`}
                        >
                            오늘 (Today) 로 이동
                        </button>
                    </div>
                </div>
                , document.body
            )}
        </div>
    );
}
